import { z } from "zod";
import isPossible from "libphonenumber-js";
import { parse, isValid } from "date-fns";
import type { PaymentAmounts } from "nautika-types";

// Schéma pour un adulte
export const adultSchema = z.object({
  firstName: z
    .string()
    .min(2, { message: "Le prénom doit contenir au moins 2 caractères" })
    .max(30, { message: "Le prénom ne peut pas dépasser 30 caractères" }),
  lastName: z.string().min(2, { message: "Le nom doit contenir au moins 2 caractères" }).max(60, { message: "Le nom ne peut pas dépasser 60 caractères" }),
  email: z.union([z.email({ message: "Adresse email invalide" }), z.literal("")]).optional(),
  phone: z.union([z.string().refine(isPossible, { message: "Numéro de téléphone invalide" }), z.literal("")]).optional(),
});

export const childSchema = z.object({
  firstName: z
    .string()
    .min(2, { message: "Le prénom doit contenir au moins 2 caractères" })
    .max(30, { message: "Le prénom ne peut pas dépasser 30 caractères" }),
  lastName: z.string().min(2, { message: "Le nom doit contenir au moins 2 caractères" }).max(60, { message: "Le nom ne peut pas dépasser 60 caractères" }),
  birthDate: z
    .string()
    .min(1, { message: "La date de naissance est requise" })
    .refine(
      (val) => {
        const parsed = parse(val, "dd/MM/yyyy", new Date());
        return isValid(parsed);
      },
      { message: "Date de naissance invalide (jj/mm/aaaa)" }
    ),
  gender: z.enum(["M", "F"], { error: "Sélectionnez un genre." }),
  groupId: z.string({ error: "Sélectionnez un groupe." }),
  notes: z.string(),
  leaveAlone: z.boolean(),
  imageRights: z.boolean(),
});

// Schéma Zod pour la validation des paiements basé sur PaymentAmounts
const paymentMethodsSchema = z.object({
  card: z.number().min(0).default(0),
  ancv: z.number().min(0).default(0),
  cash: z.number().min(0).default(0),
  check: z.number().min(0).default(0),
}) satisfies z.ZodType<PaymentAmounts>;

// Schéma simplifié pour la validation du formulaire d'inscription
export const registrationFormSchema = z
  .object({
    adults: z.array(z.object({ id: z.string() })).min(1, { message: "Au moins un adulte de confiance est requis" }),
    children: z.array(z.object({ id: z.string() })).min(1, { message: "Au moins un enfant est requis" }),
    periods: z.record(z.string(), z.array(z.string())),
    promotionId: z.string().optional(),
    price: z.number().gt(0, { message: "Indiquez le montant total de l'inscription" }),
    paymentMethods: paymentMethodsSchema,
  })
  .refine(
    (data) => {
      // Vérifier que chaque enfant a au moins une période sélectionnée
      return data.children.every((child) => {
        const periods = data.periods[child.id] || [];
        return periods.length > 0;
      });
    },
    {
      message: "Chaque enfant doit avoir au moins une période sélectionnée",
    }
  );

// Types TypeScript inférés des schémas
export type Adult = z.infer<typeof adultSchema>;
export type Child = z.infer<typeof childSchema>;
export type RegistrationFormData = z.infer<typeof registrationFormSchema>;
